/**
 * The block model behind the campaign email composer.
 *
 * Blocks are the EDITING representation. What actually sends is the derived
 * plain text (`blocksToText`), because every connected provider sends
 * text/plain today, a builder that previewed rich HTML while the customer
 * received something else would be a lie. The preview renders the same blocks
 * the send text is derived from, so the two cannot drift.
 */

export type EmailBlock =
  | { id: string; type: "heading"; text: string }
  | { id: string; type: "paragraph"; text: string }
  | { id: string; type: "button"; label: string; url: string }
  | { id: string; type: "divider" };

export const newBlock = (type: EmailBlock["type"]): EmailBlock => {
  const id = crypto.randomUUID();
  switch (type) {
    case "heading":
      return { id, type, text: "" };
    case "button":
      return { id, type, label: "", url: "" };
    case "divider":
      return { id, type };
    default:
      return { id, type: "paragraph", text: "" };
  }
};

/**
 * The send text. A button becomes "label: url", in a text/plain email the
 * URL must be visible to be clickable, and hiding it behind a label is also
 * exactly what phishing looks like, so showing both is correct twice over.
 */
export const blocksToText = (blocks: EmailBlock[]): string =>
  blocks
    .map((block) => {
      switch (block.type) {
        case "heading":
          return block.text.trim();
        case "paragraph":
          return block.text.trim();
        case "button": {
          const label = block.label.trim();
          const url = block.url.trim();
          if (!url) return label;
          return label ? `${label}: ${url}` : url;
        }
        case "divider":
          return "----------";
      }
    })
    .filter((part) => part !== "")
    .join("\n\n");

/** Parse stored blocks defensively, jsonb from the DB is untyped. */
export const parseBlocks = (raw: unknown): EmailBlock[] | null => {
  if (!Array.isArray(raw)) return null;
  const blocks: EmailBlock[] = [];
  for (const item of raw) {
    if (typeof item !== "object" || item === null) return null;
    const candidate = item as Record<string, unknown>;
    const id =
      typeof candidate.id === "string" ? candidate.id : crypto.randomUUID();
    switch (candidate.type) {
      case "heading":
      case "paragraph":
        blocks.push({
          id,
          type: candidate.type,
          text: typeof candidate.text === "string" ? candidate.text : "",
        });
        break;
      case "button":
        blocks.push({
          id,
          type: "button",
          label: typeof candidate.label === "string" ? candidate.label : "",
          url: typeof candidate.url === "string" ? candidate.url : "",
        });
        break;
      case "divider":
        blocks.push({ id, type: "divider" });
        break;
      default:
        return null;
    }
  }
  return blocks;
};

/**
 * Seed the builder from an existing plain-text body so switching a campaign
 * written in the textarea era to blocks does not blank someone's draft.
 */
export const textToBlocks = (text: string): EmailBlock[] => {
  const paragraphs = text
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (paragraphs.length === 0) return [newBlock("paragraph")];
  return paragraphs.map((part) => ({
    id: crypto.randomUUID(),
    type: "paragraph" as const,
    text: part,
  }));
};
